import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { Sheet } from "@/components/ui/sheet";
import { relativeTime } from "@/lib/format/date";
import { cn } from "@/lib/utils/cn";

/** One shaped AuditLog entry, as returned by the admin history API
 * (core_project.history.entry_to_json). */
export interface HistoryLine {
  label: string;
  kind: "text" | "bool" | "list";
  /** What the change did, decided server-side (core_project.history) so both
   * drawers tint it the same: added (green), removed (red), comment (blue),
   * changed (neutral). */
  tone?: "added" | "removed" | "changed" | "comment";
  old?: string;
  new?: string;
  old_on?: boolean;
  new_on?: boolean;
  added?: string[];
  collapsed?: boolean;
  removed?: string[];
  reordered?: boolean;
  order?: string[];
  order_from?: string[];
}

export interface HistoryEntry {
  user_label: string;
  initials: string;
  created_at: string;
  /** The AuditLog action (create, update, delete…), which colours the dot. */
  action?: string;
  lines: HistoryLine[];
  badge: { label: string; css: string } | null;
  note: string;
  item: { id: string; name: string; crumb: string } | null;
  /** Set on a catalogue deletion that can still be undone. */
  restore_id?: string | null;
  /** Who the actor was to whoever is reading — null on the back-office feeds,
   * where every actor is CQDD (see accounts.actors). */
  actor?: { label: string; css: string; detail: string } | null;
}

/** The block tint for each tone; tokens only, so both themes hold. */
const TONE_BLOCK: Record<string, string> = {
  added: "border-success/30 bg-success/10",
  removed: "border-destructive/30 bg-destructive/10",
  comment: "border-info/30 bg-info/10",
  changed: "border-border bg-muted/60",
};
const TONE_LABEL: Record<string, string> = {
  added: "text-success-ink",
  removed: "text-destructive-ink",
  comment: "text-info-ink",
  changed: "text-muted-foreground",
};

/** The timeline dot, coloured by what the entry did. */
function dotClass(action?: string) {
  if (action === "create") return "bg-success";
  if (action === "update") return "bg-warning";
  if (action === "delete" || action === "revoke") return "bg-destructive";
  return "bg-muted-foreground";
}

/** One change, as a block tinted by its tone. */
function Block({
  tone,
  label,
  children,
}: {
  tone: string;
  label: ReactNode;
  children: ReactNode;
}) {
  return (
    <div
      className={cn(
        "mt-2 rounded-md border px-2.5 py-2 text-sm break-words",
        TONE_BLOCK[tone] ?? TONE_BLOCK.changed,
      )}
    >
      <p
        className={cn(
          "mb-1 text-[10.5px] font-semibold uppercase tracking-wider",
          TONE_LABEL[tone] ?? TONE_LABEL.changed,
        )}
      >
        {label}
      </p>
      {children}
    </div>
  );
}

function Items({ names, sign }: { names: string[]; sign: "+" | "−" }) {
  return (
    <ul className="space-y-0.5">
      {names.map((name, i) => (
        <li
          key={i}
          className={cn(sign === "−" && "text-destructive-ink line-through")}
        >
          {sign} {name}
        </li>
      ))}
    </ul>
  );
}

export interface HistoryResponse {
  entries: HistoryEntry[];
  truncated: boolean;
  limit: number;
}

interface HistoryQuery {
  data?: HistoryResponse;
  isPending: boolean;
  isError: boolean;
}

/** Read-only modification-history drawer shared by the tools & financial-aids
 * SPA back-offices. Mirrors the Django `_history-rows.html` renderer. */
export function HistoryDrawer({
  title,
  onClose,
  query,
  entryActions,
}: {
  title: string;
  onClose: () => void;
  query: HistoryQuery;
  /** Controls under an entry (the catalogue's restore button). */
  entryActions?: (entry: HistoryEntry) => ReactNode;
}) {
  const { t, i18n } = useTranslation();
  const entries = query.data?.entries ?? [];

  return (
    // Opens on the right at the Django drawer's width (440px), full width on a
    // phone — the same drawer in both apps.
    <Sheet
      title={title}
      onClose={onClose}
      side="right"
      className="w-full max-w-full sm:w-[440px] sm:max-w-[92vw]"
    >
      {query.isPending ? (
        <p className="p-6 text-center text-sm text-muted-foreground">
          {t("common.history.loading")}
        </p>
      ) : query.isError ? (
        <p className="p-6 text-center text-sm text-destructive">
          {t("common.history.error")}
        </p>
      ) : entries.length === 0 ? (
        <p className="p-6 text-center text-sm italic text-muted-foreground">
          {t("common.history.empty")}
        </p>
      ) : (
        <ol className="space-y-3">
          {entries.map((e, i) => (
            <li key={i} className="relative grid grid-cols-[14px_1fr] gap-2.5">
              {i < entries.length - 1 && (
                <span
                  className="absolute bottom-[-12px] left-[6px] top-5 w-0.5 bg-border"
                  aria-hidden
                />
              )}
              <span
                className={cn(
                  "relative z-10 mt-3.5 size-3 rounded-full ring-[3px] ring-background",
                  dotClass(e.action),
                )}
                aria-hidden
              />
              <article className="min-w-0 rounded-lg border border-border bg-card p-3 shadow-sm">
                <div className="flex items-start gap-3">
                  {e.item && (
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold break-words">{e.item.name}</p>
                      {e.item.crumb && (
                        <p className="mt-0.5 text-xs text-muted-foreground break-words">
                          {e.item.crumb}
                        </p>
                      )}
                    </div>
                  )}
                  <div className="ml-auto flex max-w-[55%] shrink-0 flex-col items-end gap-1 text-right text-xs">
                    <b className="font-semibold text-foreground break-words">{e.user_label}</b>
                    {e.actor && (
                      <span
                        className={cn(
                          "rounded-full border border-border px-1.5 py-0.5",
                          "text-[10.5px] font-semibold",
                          // Same four weights the Django drawer gives them
                          // (_definition-history.scss): the partner stands out,
                          // "Vous" recedes, CQDD reads as the strongest voice.
                          e.actor.css === "is-partner" &&
                            "border-primary/30 bg-primary/10 text-primary",
                          e.actor.css === "is-self" && "text-muted-foreground/70",
                          e.actor.css === "is-admin" && "text-foreground",
                          e.actor.css === "is-member" && "text-muted-foreground",
                        )}
                      >
                        {e.actor.label}
                        {e.actor.detail ? ` · ${e.actor.detail}` : ""}
                      </span>
                    )}
                    <span className="text-muted-foreground">
                      {relativeTime(e.created_at, i18n.language)}
                    </span>
                    {e.badge && (
                      <span
                        className={cn(
                          "rounded px-1.5 py-0.5 text-[11px] font-semibold",
                          e.badge.css === "is-approved" &&
                            "bg-success/15 text-success-ink",
                          e.badge.css === "is-pending" &&
                            "bg-warning/15 text-warning-ink",
                          e.badge.css === "is-counter" &&
                            "bg-primary/15 text-primary",
                          e.badge.css === "is-deleted" &&
                            "bg-destructive/15 text-destructive-ink",
                        )}
                      >
                        {e.badge.label}
                      </span>
                    )}
                  </div>
                </div>
                {e.lines.map((line, li) => (
                  <HistoryLineBlocks key={li} line={line} t={t} />
                ))}
                {e.note && (
                  <Block tone="comment" label={t("common.history.note")}>
                    <p className="whitespace-pre-line">{e.note}</p>
                  </Block>
                )}
                {entryActions?.(e)}
              </article>
            </li>
          ))}
        </ol>
      )}
      {query.data?.truncated && (
        <p className="mt-4 text-center text-xs italic text-muted-foreground">
          {t("common.history.truncated", { limit: query.data.limit })}
        </p>
      )}
    </Sheet>
  );
}

/** A change as one block, or two when a list both gained and lost items. */
function HistoryLineBlocks({
  line,
  t,
}: {
  line: HistoryLine;
  t: (key: string, opts?: Record<string, unknown>) => string;
}) {
  const tone = line.tone ?? "changed";
  if (line.kind === "bool") {
    return (
      <Block tone={tone} label={line.label}>
        <span className="inline-flex items-center gap-1.5">
          <State on={line.old_on} t={t} />
          <span aria-hidden>→</span>
          <State on={line.new_on} t={t} />
        </span>
      </Block>
    );
  }
  if (line.kind === "list") {
    const added = line.added ?? [];
    const removed = line.removed ?? [];
    const order = line.order ?? [];
    return (
      <>
        {(added.length > 0 || removed.length > 0) &&
          // Long lists open on click. A native <details> rather than component
          // state: no JS, keyboard- and screen-reader-navigable for free, and
          // the same interaction as the Django drawer. `collapsed` is decided
          // server-side so both apps fold at the same point.
          (line.collapsed ? (
            <Block
              tone={tone}
              label={
                <details className="group normal-case tracking-normal">
                  <summary className="cursor-pointer text-[10.5px] font-semibold uppercase tracking-wider">
                    {line.label} —{" "}
                    {[
                      added.length > 0 &&
                        t("common.history.addedCount", { count: added.length }),
                      removed.length > 0 &&
                        t("common.history.removedCount", { count: removed.length }),
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </summary>
                  <div className="mt-1 text-sm font-normal text-foreground">
                    <Items names={added} sign="+" />
                    <Items names={removed} sign="−" />
                  </div>
                </details>
              }
            >
              {null}
            </Block>
          ) : (
            <>
              {added.length > 0 && (
                <Block tone="added" label={line.label}>
                  <Items names={added} sign="+" />
                </Block>
              )}
              {removed.length > 0 && (
                <Block tone="removed" label={line.label}>
                  <Items names={removed} sign="−" />
                </Block>
              )}
            </>
          ))}
        {(line.reordered || (added.length === 0 && removed.length === 0)) && (
          <Block tone="changed" label={line.label}>
            {line.reordered ? (
              <span className="text-xs text-muted-foreground">
                <span className="font-medium text-foreground">
                  {t("common.history.orderChanged")}
                </span>
                <ol className="my-0.5 list-decimal pl-5 text-foreground">
                  {order.map((name, i) => (
                    <li key={`o${i}`}>{name}</li>
                  ))}
                </ol>
                <span className="italic">
                  {t("common.history.previously", {
                    list: (line.order_from ?? []).join(", "),
                  })}
                </span>
              </span>
            ) : (
              <span className="text-xs italic text-muted-foreground">
                {t("common.history.reorderOnly")}
              </span>
            )}
          </Block>
        )}
      </>
    );
  }
  return (
    <Block tone={tone} label={line.label}>
      {tone === "added" || tone === "comment" ? (
        <p className="whitespace-pre-line">{line.new}</p>
      ) : tone === "removed" ? (
        <p className="whitespace-pre-line text-destructive-ink line-through">{line.old}</p>
      ) : (
        <span className="inline-flex flex-wrap items-center gap-1.5">
          <span className="whitespace-pre-line text-muted-foreground line-through">
            {line.old}
          </span>
          <span aria-hidden>→</span>
          <span className="whitespace-pre-line font-semibold">{line.new}</span>
        </span>
      )}
    </Block>
  );
}

function State({ on, t }: { on?: boolean; t: (key: string) => string }) {
  return (
    <span
      className={cn(
        "rounded-full px-2 py-0.5 text-xs font-semibold",
        on ? "bg-success/15 text-success" : "bg-muted text-muted-foreground",
      )}
    >
      {on ? t("common.history.on") : t("common.history.off")}
    </span>
  );
}
